import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OverlayTrigger, Popover, Table } from 'react-bootstrap';
import { FaDesktop, FaMobileScreenButton, FaWifi } from 'react-icons/fa6';
import { MdHttp, MdLink, MdOutlineCookie } from 'react-icons/md';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import type { ReactNode } from 'react';
import type {
  AnalysisCookie,
  AnalysisHeaderPair,
  AnalysisSettings,
  ReportLanguage
} from '@/app/PageSpeed/api/types/analysisApiTypes';

const COPY = {
  fa: {
    url: 'آدرس',
    server: 'سرور',
    device: 'دستگاه',
    internet: 'اینترنت',
    cookies: 'کوکی',
    headers: 'هدر',
    desktop: 'رایانه',
    mobile: 'گوشی',
    ir: 'ایران',
    not_ir: 'خارج',
    google: 'گوگل',
    unknown: 'نامشخص',
    openUrl: 'باز کردن آدرس',
    cookieCount: (count: number) => `${count} مورد`,
    headerCount: (count: number) => `${count} مورد`
  },
  en: {
    url: 'URL',
    server: 'Server',
    device: 'Device',
    internet: 'Network',
    cookies: 'Cookies',
    headers: 'Headers',
    desktop: 'Desktop',
    mobile: 'Mobile',
    ir: 'Iran',
    not_ir: 'Outside',
    google: 'Google',
    unknown: 'Unknown',
    openUrl: 'Open URL',
    cookieCount: (count: number) => `${count} item${count === 1 ? '' : 's'}`,
    headerCount: (count: number) => `${count} item${count === 1 ? '' : 's'}`
  }
} as const;

type PublicReportSettingsProps = {
  settings?: AnalysisSettings;
  language: ReportLanguage;
  isLoading?: boolean;
};

const isDesktopDevice = (deviceType?: unknown) =>
  deviceType === 'desktop' || deviceType === 1 || deviceType === '1';

const formatUrlLabel = (url?: string) => {
  if (!url) return '';
  try {
    const parsed = new URL(url);
    return `${parsed.hostname}${parsed.pathname}`.replace(/\/$/, '');
  } catch {
    return url.replace(/^https?:\/\//, '');
  }
};

const SettingChip = ({
  icon,
  label,
  value,
  title,
  className = ''
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  title?: string;
  className?: string;
}) => (
  <div
    className={`d-flex align-items-center gap-2 px-3 py-2 rounded-3 border bg-100 dark__bg-1100 ${className}`}
    title={title}
    style={{ minWidth: 0 }}
  >
    <div className="icon-item icon-item-sm border shadow-none flex-shrink-0 text-primary">
      {icon}
    </div>
    <div className="min-w-0">
      <div className="fs--2 text-600 text-nowrap">{label}</div>
      <div className="fs--1 fw-medium text-900 text-truncate">{value}</div>
    </div>
  </div>
);

const DetailsPopoverChip = ({
  icon,
  label,
  value,
  title,
  children
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  title: string;
  children: ReactNode;
}) => (
  <OverlayTrigger
    trigger="click"
    rootClose
    placement="bottom"
    overlay={
      <Popover
        id={`public-report-${label}`}
        style={{
          width: 'min(420px, calc(100vw - 32px))',
          maxWidth: 'calc(100vw - 32px)'
        }}
      >
        <Popover.Header as="h6" className="mb-0">
          {title}
        </Popover.Header>
        <Popover.Body className="p-0">{children}</Popover.Body>
      </Popover>
    }
  >
    <button
      type="button"
      className="d-flex align-items-center gap-2 px-3 py-2 rounded-3 border bg-100 dark__bg-1100 text-start text-reset"
      style={{
        minWidth: 0,
        appearance: 'none',
        font: 'inherit',
        cursor: 'pointer'
      }}
    >
      <div className="icon-item icon-item-sm border shadow-none flex-shrink-0 text-primary">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="fs--2 text-600 text-nowrap">{label}</div>
        <div className="fs--1 fw-medium text-900 text-truncate">{value}</div>
      </div>
    </button>
  </OverlayTrigger>
);

const LocationIcon = ({ locationCode }: { locationCode?: string }) => {
  if (locationCode === 'not_ir') {
    return <FontAwesomeIcon icon="globe" />;
  }
  if (locationCode === 'google') {
    return <FontAwesomeIcon icon={['fab', 'google']} />;
  }
  if (locationCode) {
    return (
      <img
        src={`https://static.mizfa.tools/flags/${locationCode}.webp`}
        alt={locationCode}
        width={18}
        height={12}
        style={{ objectFit: 'cover', borderRadius: 2 }}
      />
    );
  }
  return <FontAwesomeIcon icon="globe" />;
};

const PublicReportSettings = ({
  settings,
  language,
  isLoading = false
}: PublicReportSettingsProps) => {
  const copy = COPY[language] || COPY.fa;

  if (isLoading) {
    return (
      <div className="d-flex flex-wrap align-items-stretch gap-2 flex-grow-1">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="d-flex align-items-center gap-2 px-3 py-2 rounded-3 border bg-100 dark__bg-1100"
          >
            <TextLoading width={30} height={30} isWidthPx />
            <div>
              <TextLoading width={42} height={10} isWidthPx />
              <TextLoading width={72} height={14} isWidthPx />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!settings) return null;

  const {
    url,
    location_code,
    device_type,
    internet_type,
    cookies = [],
    headers = []
  } = settings;
  const desktop = isDesktopDevice(device_type);
  const locationLabel =
    location_code === 'ir'
      ? copy.ir
      : location_code === 'not_ir'
        ? copy.not_ir
        : location_code === 'google'
          ? copy.google
          : location_code || copy.unknown;
  const cookieItems = (cookies || []).filter(Boolean) as AnalysisCookie[];
  const headerItems = (headers || []).filter(Boolean) as AnalysisHeaderPair[];

  return (
    <div className="d-flex flex-wrap align-items-stretch gap-2 flex-grow-1">
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-decoration-none text-reset d-flex"
          title={copy.openUrl}
          style={{ maxWidth: 'min(320px, 100%)' }}
        >
          <SettingChip
            icon={<MdLink size={16} />}
            label={copy.url}
            title={url}
            value={
              <span dir="ltr" className="d-inline-block text-truncate w-100">
                {formatUrlLabel(url)}
              </span>
            }
          />
        </a>
      ) : null}

      <SettingChip
        icon={<LocationIcon locationCode={location_code} />}
        label={copy.server}
        value={locationLabel}
      />

      <SettingChip
        icon={desktop ? <FaDesktop /> : <FaMobileScreenButton />}
        label={copy.device}
        value={desktop ? copy.desktop : copy.mobile}
      />

      <SettingChip
        icon={<FaWifi />}
        label={copy.internet}
        value={(internet_type || copy.unknown).toUpperCase()}
      />

      {cookieItems.length > 0 ? (
        <DetailsPopoverChip
          icon={<MdOutlineCookie size={16} />}
          label={copy.cookies}
          value={copy.cookieCount(cookieItems.length)}
          title={copy.cookies}
        >
          <div className="p-2" style={{ maxHeight: 240, overflow: 'auto' }}>
            <Table size="sm" className="mb-0 fs--1" dir="ltr">
              <thead>
                <tr>
                  <th>name</th>
                  <th>value</th>
                  <th>domain</th>
                </tr>
              </thead>
              <tbody>
                {cookieItems.map((item, index) => (
                  <tr key={`${item.name || 'cookie'}-${index}`}>
                    <td>{item.name || '-'}</td>
                    <td className="text-truncate" style={{ maxWidth: 160 }}>
                      {String(item.value ?? '-')}
                    </td>
                    <td>{item.domain || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </DetailsPopoverChip>
      ) : null}

      {headerItems.length > 0 ? (
        <DetailsPopoverChip
          icon={<MdHttp size={16} />}
          label={copy.headers}
          value={copy.headerCount(headerItems.length)}
          title={copy.headers}
        >
          <div className="p-2" style={{ maxHeight: 240, overflow: 'auto' }}>
            <Table size="sm" className="mb-0 fs--1" dir="ltr">
              <thead>
                <tr>
                  <th>name</th>
                  <th>value</th>
                </tr>
              </thead>
              <tbody>
                {headerItems.map((item, index) => (
                  <tr key={`${item.name || 'header'}-${index}`}>
                    <td>{item.name || '-'}</td>
                    <td className="text-truncate" style={{ maxWidth: 220 }}>
                      {item.value || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </DetailsPopoverChip>
      ) : null}
    </div>
  );
};

export default PublicReportSettings;
